import React, { useEffect } from 'react'
import FlagProvider, { useUnleashContext } from '@unleash/proxy-client-react'
import type { IConfig } from 'unleash-proxy-client'

import { env } from '@/lib/env'
import { useAuth } from '@/shared/ui/providers/AuthSessionProvider'

const url = env.NEXT_PUBLIC_UNLEASH_URL
const clientKey = env.NEXT_PUBLIC_UNLEASH_CLIENT_KEY

/**
 * Unleash is optional: when the proxy URL or the public client key is not
 * configured, the provider still mounts (so `useFlag` never crashes) but the
 * client is never started — every flag evaluates to its safe default (false).
 */
const isUnleashConfigured = Boolean(url && clientKey)

const config: IConfig = {
  // The SDK constructor rejects empty strings, so feed it inert placeholders
  // when unconfigured — startClient is false in that case, no request is made.
  url: url || 'http://localhost/disabled',
  clientKey: clientKey || 'disabled',
  appName: env.NEXT_PUBLIC_UNLEASH_APP_NAME || 'medconnect-patient',
  environment: env.NEXT_PUBLIC_UNLEASH_ENVIRONMENT || 'development',
  refreshInterval: 15, // seconds
}

/**
 * Pushes the authenticated patient into the Unleash context so flags can
 * target by user / tenant, and resets it on logout.
 *
 * Must be mounted below both UnleashProvider and AuthSessionProvider
 * (it is rendered from App.tsx, not from UnleashProvider itself).
 */
export function UnleashContextSync() {
  const updateContext = useUnleashContext()
  const { isAuthenticated, user } = useAuth()

  const userId = user?.sub

  useEffect(() => {
    if (!isUnleashConfigured) return
    if (isAuthenticated && userId) {
      updateContext({
        userId,
        properties: {
          tenantId: env.NEXT_PUBLIC_KEYCLOAK_REALM,
        },
      })
    } else {
      updateContext({ userId: undefined, properties: {} })
    }
  }, [updateContext, isAuthenticated, userId])

  return null
}

type UnleashProviderProps = {
  children: React.ReactNode
}

export function UnleashProvider({ children }: UnleashProviderProps) {
  return (
    <FlagProvider config={config} startClient={isUnleashConfigured}>
      {children}
    </FlagProvider>
  )
}

export default UnleashProvider
